import { ReactNode, useCallback, useMemo } from "react";
import { useSnackbar } from "notistack";
import { useQuery } from "@tanstack/react-query";
import {
  createGridLayoutRestRequest,
  GridLayoutNotify,
  GridLayoutProvider,
} from "@freyr/freyr-react-common/grid-layout";
import service from "../../api";
import { fetchFeatureFlag } from "../../api/admin";
import { useCookieData } from "../../hooks/useCookieData";

/** Tenant feature flag (moduleId=7) that turns Save Grid Layout on. */
export const SAVE_GRID_LAYOUT_FLAG_NAME = "enableSaveGridLayout";

const gridLayoutRequest = createGridLayoutRestRequest({
  send: (config) => service.request({ ...config, silent: true }),
});

export default function ProductGridLayoutProvider({
  children,
}: {
  children?: ReactNode;
}) {
  const { domain, tenantId } = useCookieData();
  // Same query key as useTenantModuleFeatureFlags (shared cache), but read-only: this does not
  // merge the tenant flags into the global store on pages that did not load them before.
  const flagQuery = useQuery({
    queryKey: ["fetchFeatureFlag", tenantId],
    queryFn: () => fetchFeatureFlag(tenantId!),
    enabled: !!tenantId,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const flagEnabled =
    Number(flagQuery.data?.code) === 200 &&
    (flagQuery.data?.data?.featureFlags ?? []).some(
      (item: { name?: string; enabled?: boolean }) =>
        item?.name === SAVE_GRID_LAYOUT_FLAG_NAME && item.enabled === true,
    );
  // A failed flag request counts as settled (flag off) so grids never wait forever.
  const flagReady = !tenantId || flagQuery.isSuccess || flagQuery.isError;
  const { enqueueSnackbar } = useSnackbar();

  const featureFlag = useMemo(
    () => ({ enabled: flagEnabled, ready: flagReady }),
    [flagEnabled, flagReady],
  );

  const notify = useCallback<GridLayoutNotify>(
    (message, variant) => {
      enqueueSnackbar(message, { variant });
    },
    [enqueueSnackbar],
  );

  return (
    <GridLayoutProvider
      hostModule="product"
      featureFlag={featureFlag}
      request={gridLayoutRequest}
      domain={domain ?? ""}
      notify={notify}
    >
      {children}
    </GridLayoutProvider>
  );
}
